import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpFromLine, WifiOff, ShieldAlert, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, LoadingBlock, EmptyState, formatDateTime, formatCurrency } from "@/components/Common";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";

const WD_STATUS = {
  SUBMITTED: { it: "In attesa", en: "Pending", de: "Ausstehend" },
  PENDING_REVIEW: { it: "In revisione", en: "In review", de: "In Prüfung" },
  ADDITIONAL_VERIFICATION_REQUIRED: { it: "Verifica richiesta", en: "Verification required", de: "Verifizierung nötig" },
  APPROVED: { it: "Approvato", en: "Approved", de: "Genehmigt" },
  PROCESSING: { it: "In elaborazione", en: "Processing", de: "In Bearbeitung" },
  COMPLETED: { it: "Completato", en: "Completed", de: "Abgeschlossen" },
  REJECTED: { it: "Rifiutato", en: "Rejected", de: "Abgelehnt" },
  CANCELLED: { it: "Annullato", en: "Cancelled", de: "Storniert" },
};

export default function Withdraw() {
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de || en) : it);
  const [cfg, setCfg] = useState(null);
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [f, setF] = useState({ amount: "", currency: "EUR", destination_type: "crypto", destination: "", network: "", asset: "", confirm_ownership: false, terms: false });

  const load = () => {
    api.get("/portal/withdrawal-config").then(({ data }) => { setCfg(data); setF((s) => ({ ...s, currency: data.base_currency || "EUR" })); }).catch(() => setCfg(false));
    api.get("/portal/balance").then(({ data }) => setBalance(data)).catch(() => {});
    api.get("/withdrawals").then(({ data }) => setHistory(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (cfg === null) return <LoadingBlock />;
  if (cfg === false) return <EmptyState title={L("Configurazione non disponibile", "Configuration unavailable", "Konfiguration nicht verfügbar")} />;

  const amount = parseFloat(f.amount) || 0;
  const pctFee = +(amount * (cfg.percent || 0) / 100).toFixed(2);
  const totalFee = +(pctFee + (cfg.fixed || 0)).toFixed(2);
  const net = amount > 0 ? +(amount - totalFee).toFixed(2) : 0;
  const avail = balance?.available ?? 0;
  const overBalance = amount > avail;
  const blocked = cfg.account_status === "blocked" || cfg.account_status === "archived";
  const suspended = cfg.account_status === "suspended";
  const needsVerification = cfg.require_verification && cfg.verification_status !== "verified";
  const enabled = cfg.enabled && !blocked && !suspended;
  const msg = cfg.withdrawal_message;
  const msgText = msg ? ((lang === "de" ? msg.text_de : lang === "en" ? msg.text_en : msg.text_it) || msg.text_de || msg.text_it || "") : "";
  const styleMap = { informativo: "border-cyanx/30 bg-cyanx/10 text-cyanx", avviso: "border-gold/30 bg-gold/10 text-gold", importante: "border-red-500/30 bg-red-500/10 text-red-300" };
  const msgStyle = styleMap[msg?.style] || styleMap.informativo;

  const cancel = async (id) => {
    try { await api.post(`/withdrawals/${id}/cancel`); toast.success(L("Prelievo annullato", "Withdrawal cancelled", "Auszahlung storniert")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    try {
      await api.post("/withdrawals", { amount, currency: f.currency, destination_type: f.destination_type, destination: f.destination, network: f.network, asset: f.asset, confirm_ownership: f.confirm_ownership });
      toast.success(L("Richiesta di prelievo inviata", "Withdrawal request submitted", "Auszahlungsanfrage gesendet"));
      setF((s) => ({ ...s, amount: "", destination: "", confirm_ownership: false, terms: false })); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Prelievo", "Withdraw", "Auszahlung")}</h2>

      {blocked && <Banner testid="withdraw-blocked" text={L("Il tuo account è bloccato. Contatta l'assistenza.", "Your account is blocked. Contact support.", "Dein Konto ist gesperrt. Kontaktiere den Support.")} />}
      {suspended && <Banner testid="withdraw-suspended" text={L("Account sospeso: le operazioni finanziarie sono disabilitate.", "Account suspended: financial operations are disabled.", "Konto gesperrt: Finanzvorgänge sind deaktiviert.")} />}
      {!enabled && !blocked && !suspended && (
        <Card className="p-8 text-center" data-testid="withdraw-disabled">
          <WifiOff className="mx-auto h-8 w-8 text-slate-500" />
          <p className="mt-3 text-slate-300">{L("Prelievi non abilitati", "Withdrawals not enabled", "Auszahlungen nicht aktiviert")}</p>
        </Card>
      )}

      {enabled && (
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-white/10 bg-navy px-4 py-3">
            <span className="text-sm text-slate-400">{L("Saldo disponibile", "Available balance", "Verfügbares Guthaben")}</span>
            <div className="text-right">
              <span className="font-mono text-white block" data-testid="withdraw-available">{formatCurrency(avail, f.currency)}</span>
              {balance?.reserved ? <span className="text-xs text-slate-500">{L("Bloccato", "Reserved", "Reserviert")}: {formatCurrency(balance.reserved, f.currency)}</span> : null}
            </div>
          </div>

          {needsVerification && (
            <div className="flex items-start gap-2 rounded-lg border border-gold/30 bg-gold/10 p-3 text-sm text-gold" data-testid="withdraw-need-verification">
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />{L("Verifica dell'account necessaria per richiedere un prelievo.", "Account verification required to request a withdrawal.", "Kontoverifizierung erforderlich, um eine Auszahlung anzufordern.")}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300">{L("Importo", "Amount", "Betrag")}</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="withdraw-amount" /></div>
            <div><Label className="text-slate-300">{L("Tipo destinazione", "Destination type", "Zieltyp")}</Label>
              <select value={f.destination_type} onChange={(e) => setF({ ...f, destination_type: e.target.value })} className="mt-1.5 w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="withdraw-dest-type">
                <option value="crypto">{L("Wallet crypto", "Crypto wallet", "Krypto-Wallet")}</option><option value="bank">{L("Conto bancario", "Bank account", "Bankkonto")}</option>
              </select>
            </div>
          </div>
          <div><Label className="text-slate-300">{f.destination_type === "crypto" ? L("Indirizzo wallet", "Wallet address", "Wallet-Adresse") : L("IBAN / coordinate", "IBAN / details", "IBAN / Daten")}</Label><Input value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} className="mt-1.5 bg-navy border-white/10 font-mono text-xs" data-testid="withdraw-destination" /></div>
          {f.destination_type === "crypto" && <div><Label className="text-slate-300">{L("Rete", "Network", "Netzwerk")}</Label><Input value={f.network} onChange={(e) => setF({ ...f, network: e.target.value })} placeholder="Bitcoin, Ethereum (ERC20)…" className="mt-1.5 bg-navy border-white/10" data-testid="withdraw-network" /></div>}

          <div className="rounded-lg border border-white/10 bg-navy p-4 text-sm space-y-1.5" data-testid="withdraw-fees">
            <Row l={L("Importo richiesto", "Requested amount", "Angeforderter Betrag")} v={formatCurrency(amount, f.currency)} />
            <Row l={`${L("Commissione", "Commission", "Provision")} (${cfg.percent || 0}%)`} v={formatCurrency(pctFee, f.currency)} />
            <Row l={L("Commissione fissa / rete", "Fixed / network fee", "Feste / Netzwerkgebühr")} v={formatCurrency(cfg.fixed || 0, f.currency)} />
            <div className="border-t border-white/10 pt-1.5"><Row l={L("Importo netto", "Net amount", "Nettobetrag")} v={<span className="text-emerald-400 font-medium" data-testid="withdraw-net">{formatCurrency(net, f.currency)}</span>} /></div>
          </div>

          {msg && msg.enabled && (msgText || msg.title) && (
            <div className={`rounded-lg border p-4 text-sm ${msgStyle}`} data-testid="withdraw-admin-message">
              {msg.title && <p className="font-medium mb-1">{msg.title}</p>}
              {msgText && <p className="whitespace-pre-line">{msgText}</p>}
            </div>
          )}

          <label className="flex items-start gap-2.5 text-sm text-slate-300"><Checkbox checked={f.confirm_ownership} onCheckedChange={(v) => setF({ ...f, confirm_ownership: !!v })} data-testid="withdraw-ownership" className="mt-0.5" />{L("Confermo di essere il titolare della destinazione", "I confirm I own the destination", "Ich bestätige, dass ich Inhaber des Ziels bin")}</label>
          <label className="flex items-start gap-2.5 text-sm text-slate-300"><Checkbox checked={f.terms} onCheckedChange={(v) => setF({ ...f, terms: !!v })} data-testid="withdraw-terms" className="mt-0.5" />{L("Accetto le condizioni e la commissione indicata", "I accept the terms and the stated commission", "Ich akzeptiere die Bedingungen und die angegebene Provision")}</label>
          {overBalance && amount > 0 && <p className="text-sm text-red-400" data-testid="withdraw-over">{L("L'importo supera il saldo disponibile", "Amount exceeds available balance", "Betrag übersteigt verfügbares Guthaben")}</p>}
          <Button onClick={submit} disabled={!amount || overBalance || !f.confirm_ownership || !f.terms || !f.destination || needsVerification} className="bg-gold text-navy hover:bg-gold-hover" data-testid="withdraw-submit">
            <ArrowUpFromLine className="h-4 w-4 mr-1.5" />{L("Invia richiesta", "Submit request", "Anfrage senden")}
          </Button>
        </Card>
      )}

      <Card className="p-6">
        <h3 className="font-heading text-lg text-white mb-4">{L("Storico prelievi", "Withdrawal history", "Auszahlungsverlauf")}</h3>
        {history.length === 0 ? <EmptyState title={L("Nessun prelievo", "No withdrawals", "Keine Auszahlungen")} /> : (
          <div className="space-y-2">
            {history.map((w) => (
              <div key={w.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-navy px-4 py-3 text-sm" data-testid={`withdrawal-${w.id}`}>
                <div><p className="text-white">{formatCurrency(w.amount, w.currency)} → {L("netto", "net", "netto")} {formatCurrency(w.net_amount, w.currency)}</p><p className="text-xs text-slate-500">{formatDateTime(w.created_at)}{w.client_explanation ? ` · ${w.client_explanation}` : ""}</p></div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="text-xs text-gold">{WD_STATUS[w.status]?.[lang] || WD_STATUS[w.status]?.it || w.status}</span>
                  {["SUBMITTED", "PENDING_REVIEW", "ADDITIONAL_VERIFICATION_REQUIRED"].includes(w.status) && (
                    <button onClick={() => cancel(w.id)} title={L("Annulla", "Cancel", "Stornieren")} className="text-slate-400 hover:text-red-400" data-testid={`withdraw-cancel-${w.id}`}><XCircle className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Row({ l, v }) {
  return <div className="flex items-center justify-between"><span className="text-slate-400">{l}</span><span className="text-slate-200">{v}</span></div>;
}

function Banner({ text, testid }) {
  return <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300" data-testid={testid}><ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />{text}</div>;
}
